<template>
  <div v-if="list.length" :ref="'v-slideshow-' + id" class="v-slideshow" :class="[wrapClass, { 'is-focus': isFocus }]">
    <div v-touch-outside="touchOutside" class="v-slideshow-content">
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
      <div
        ref="v-slideshow-wrapper"
        class="v-slideshow-wrapper"
        @mousedown.passive="(e) => scrollerTouchStart(e, true)"
        @mouseup.passive="(e) => scrollerTouchEnd(e, true)"
        @mousemove.passive="(e) => scrollerTouchMove(e, true)"
        @click.capture="preventClickAfterDrag"
        @touchend.passive="touchInside"
        @mouseenter="scrollMouseenter"
        @mouseleave="scrollMouseleave($event)"
        @focus="scrollMouseenter"
        @blur="scrollMouseleave($event)"
      >
        <div
          ref="v-scroller"
          :style="scrollerStyle()"
          class="v-scroller"
          @touchstart.passive="scrollerTouchStart"
          @touchmove.passive="scrollerTouchMove"
          @touchend.passive="scrollerTouchEnd"
        >
          <ul class="grid-x v-slideshow" aria-label="slideshow">
            <li v-if="!isMounted" class="active cell small-12 slideshow-item" data-slideshow-item>
              <slot name="item" :data="list[0]" :index="0" />
            </li>
            <template v-else>
              <li
                v-for="(item, index) in list"
                :id="`slide-${currentPage}`"
                :key="index"
                class="cell small-12 slideshow-item"
                data-slideshow-item
                :class="currentPage === index + 1 || prevIndex === index + 1 ? 'active' : ''"
                v-bind="{
                  ...(currentIndicator === index && {
                    'aria-live': 'polite',
                    'aria-roledescription': list[currentIndicator]?.title,
                  }),
                }"
                role="group"
                :inert="index + 1 !== currentPage || undefined"
                :aria-atomic="currentIndicator === index"
              >
                <slot name="item" :data="item" :index="index" />
              </li>
            </template>
          </ul>
        </div>

        <slot name="zoom-icon" />
      </div>
      <div
        v-if="showIndicator && numberOfPages > 1"
        class="v-dot-indicator-wrapper padding-top-xs"
        :class="{
          'mobile-dots': numberOfPages > maxDotImageNumber,
          'padding-bottom-m': inOverlay,
        }"
      >
        <ul class="grid-x align-center align-center-middle">
          <li
            v-for="index in altDots"
            :id="index"
            :key="index"
            tabindex="0"
            role="button"
            :aria-label="'Moved to ' + index + numberSuffix(index) + ' slide'"
            :class="{ active: index === currentPage }"
            @click.prevent="changeDot(index)"
            @keypress.enter="changeDot(index)"
          ></li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { useId, inject } from 'vue';
import { useDeviceType } from '~/composables/utils/useDeviceType.js';

export default {
  name: 'Slideshow',
  directives: {
    'touch-outside': {
      beforeMount: (el, binding) => {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('touchend', el.clickOutsideEvent);
      },
      unmounted: (el) => {
        document.removeEventListener('touchend', el.clickOutsideEvent);
      },
    },
  },
  props: {
    list: {
      type: Array,
      required: true,
    },
    autoplayMode: {
      type: String,
      default: 'auto',
      /**
       * @param {string} val
       */
      validator: (val) => ['auto', 'manual', 'manual-hover'].includes(val),
    },
    backToFirst: {
      type: Boolean,
      default: false,
    },
    showIndicator: {
      type: Boolean,
      default: true,
    },
    autoplayInterval: {
      type: Number,
      default: 5000,
      /**
       * @param {number} value
       */
      validator(value) {
        return value >= 800;
      },
    },
    thresholdLimit: {
      type: Number,
      default: 0.5,
      /**
       * @param {number} value
       */
      validator(value) {
        return value >= 0 && value <= 1;
      },
    },
    wrapClass: {
      type: String,
      default: '',
    },
    isZoomedIn: {
      type: Boolean,
      default: false,
    },
    inOverlay: {
      type: Boolean,
      default: false,
    },
    initialIndex: {
      type: Number,
      default: 1,
    },
    firstAltDot: {
      type: Number,
      default: 1,
    },
    topLevelZoomerMobile: {
      type: Boolean,
      default: false,
    },
    isAutoplayed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['slideshow-change', 'load', 'update-first-alt-dot'],
  setup() {
    const { isMobile } = useDeviceType();
    const triggerAltImageClickOnce = inject('triggerAltImageClickOnce', () => {});
    const triggerLastAltImageClickOnce = inject('triggerLastAltImageClickOnce', () => {});
    return { isMobile, triggerAltImageClickOnce, triggerLastAltImageClickOnce };
  },
  data(props) {
    const uuid = useId();
    const maxDotImageNumber = 10;
    const altDotsEnd = Math.min(props.list.length, maxDotImageNumber + props.firstAltDot - 1);
    return {
      id: uuid,
      numberOfPages: props.list.length,
      altDots: Array.from({ length: Math.max(altDotsEnd - props.firstAltDot + 1, 0) }, (_, i) => props.firstAltDot + i),
      maxDotImageNumber,
      itemsLength: 3,
      scrollerWidth: null,
      childWidth: 0,
      currentPage: props.initialIndex,
      scrollerX: 0,
      timer: null,
      noTransition: false,
      sliding: false,
      isFocus: false,
      prevIndex: -1,
      showControl: '',
      buttonFocus: false,
      hoverState: false,
      currentIndicator: '',
      dragging: false,
      hasBeenDragged: false,
      wrapWidth: null,
      isMounted: false,
      isScrollingOnMobile: false,
      isSwipingOnMobile: false,
      firstAltDotValue: props.firstAltDot,
      instance: `id${uuid}`,
    };
  },
  watch: {
    list(newList, oldList) {
      if (newList !== oldList) {
        this?.change(1);
      }
    },
    isAutoplayed(newVal) {
      if (newVal) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    },
  },
  mounted() {
    this.isMounted = true;

    if (this.list.length) {
      this.eventHandler();
    }
  },
  beforeUnmount() {
    this.removeDocumentMouseListeners();
  },
  beforeUpdate() {
    if (this.numberOfPages !== this.list.length) {
      this.init();
    }
  },
  methods: {
    /**
     * @param {number} start
     * @param {number} stop
     * @return {number[]}
     */
    arrayRange(start, stop) {
      return Array.from({ length: stop - start + 1 }, (_, index) => start + index);
    },
    touchOutside(event) {
      const target = event?.target?.className;
      if (target && target !== 'video-control-play-dark-medium' && this.autoplayMode === 'auto') {
        this.startAutoplay();
      } else if (this.autoplayMode === 'manual-hover') {
        this.stopAutoplay();
        if (this.backToFirst) {
          this?.change(1);
        }
      } else if (this.autoplayMode === 'manual' && this.backToFirst) {
        this?.change(1);
      }
    },
    documentMouseUp(event) {
      this.scrollerTouchEnd(event, true);
    },
    documentMouseMove(event) {
      this.scrollerTouchMove(event, true);
    },
    addDocumentMouseListeners() {
      document.addEventListener('mouseup', this.documentMouseUp);
      document.addEventListener('mousemove', this.documentMouseMove);
    },
    removeDocumentMouseListeners() {
      document.removeEventListener('mouseup', this.documentMouseUp);
      document.removeEventListener('mousemove', this.documentMouseMove);
    },

    touchInside() {
      if (this.autoplayMode === 'auto') {
        this.stopAutoplay();
      } else if (this.autoplayMode === 'manual-hover') {
        this.startAutoplay();
      }
    },
    scrollerStyle() {
      if (this.scrollerWidth != null) {
        return `
            width: ${this.scrollerWidth}px;
            transform: translateX(${this.scrollerX}px);
            transition-duration: ${this.noTransition || !this.sliding ? 0 : 300}ms;
            `;
      }
      return null;
    },
    resetSwipeAndScrollAction() {
      this.isScrollingOnMobile = false;
      this.isSwipingOnMobile = false;
    },
    scrollerTouchStart(event, isMouse) {
      if (isMouse) {
        this.addDocumentMouseListeners();
      }
      if (this.topLevelZoomerMobile && this.isZoomedIn) return;
      this.resetSwipeAndScrollAction();

      if (this.list.length < 2) return;

      this.dragging = true;
      this.startX = this.scrollerX; // preserve current scroller position
      this.touchstartX = isMouse ? event.screenX : event.changedTouches[0].screenX;
      this.touchstartY = isMouse ? event.screenY : event.changedTouches[0].screenY;
    },
    /**
     * @param {number} distanceX
     */
    applySwipeTransformDuringMove(distanceX) {
      const nextX = this.startX - distanceX;
      this.hasBeenDragged = true;
      this.scrollerX = nextX;
      if (nextX > 0 || nextX < this.wrapWidth - this.scrollerWidth) return;
      this.prevIndex = this.currentPage + (distanceX > 0 ? 1 : -1);
    },
    scrollerTouchMove(event, isMouse) {
      if (this.topLevelZoomerMobile && this.isZoomedIn) return;
      if (this.list.length < 2 || !this.dragging || this.isZoomedIn) {
        return;
      }

      this.touchmoveX = isMouse ? event.screenX : event.changedTouches[0].screenX;
      this.touchmoveY = isMouse ? event.screenY : event.changedTouches[0].screenY;
      const distanceX = this.touchstartX - this.touchmoveX;
      const distanceY = this.touchstartY - this.touchmoveY;

      if (this.isMobile && Math.abs(distanceY) > Math.abs(distanceX)) {
        this.isScrollingOnMobile = true;
        this.isSwipingOnMobile = false;
        return;
      }

      // If we're scrolling, ensure swipe is not processed
      if (this.isScrollingOnMobile) {
        this.isSwipingOnMobile = false;
        return;
      }

      // Determine if the swipe should be processed
      if (!this.isSwipingOnMobile && !this.isScrollingOnMobile) this.isSwipingOnMobile = true;

      // Prevent default action only for swiping
      if (this.isSwipingOnMobile) {
        this.applySwipeTransformDuringMove(distanceX);
      }
    },
    scrollerTouchEnd(event, isMouse) {
      if (!this.dragging || this.isZoomedIn) {
        return;
      }

      this.touchendX = isMouse ? event.screenX : event.changedTouches[0].screenX;
      this.touchendY = isMouse ? event.screenY : event.changedTouches[0].screenY;

      const distanceX = this.touchstartX - this.touchendX;
      const distanceY = this.touchstartY - this.touchendY;

      if (Math.abs(distanceY) && this.isScrollingOnMobile) {
        return;
      }
      this.handleSwipe(distanceX);
      this.resetSwipeAndScrollAction();
      this.dragging = false;
      if (!isMouse) {
        this.hasBeenDragged = false;
      }
      if (isMouse) {
        this.removeDocumentMouseListeners();
      }
    },
    windowSize() {
      if (window.innerWidth < 600) return 'small';
      if (window.innerWidth >= 600 && window.innerWidth < 1024) {
        return 'medium';
      }
      return 'large';
    },
    preventClickAfterDrag(event) {
      if (this.hasBeenDragged) {
        event.stopImmediatePropagation();
        event.preventDefault();
        this.hasBeenDragged = false;
      }
    },

    /**
     * @param {number} swipeDistance
     */
    handleSwipe(swipeDistance) {
      if (this.windowSize() === 'large' || this.windowSize() === 'medium') {
        if (swipeDistance > 0) {
          this.move(1);
        } else if (swipeDistance < 0) {
          this.move(-1);
        }
      } else if (this.windowSize() === 'small') {
        const threshold = 1 - this.thresholdLimit;
        const distance = Math.abs(this.touchstartX - this.touchendX);
        const hasPassedThreshold = distance > this.childWidth * threshold;
        if (hasPassedThreshold) {
          if (this.touchendX < this.touchstartX) {
            // swiped left
            if (!this.move(1)) {
              this.scrollerX = this.startX;
            }
          } else if (this.touchendX > this.touchstartX) {
            // swiped right
            if (!this.move(-1)) {
              this.scrollerX = this.startX;
            }
          }
        } else {
          this.scrollerX = this.startX;
          this.prevIndex = -1;
        }
      }
    },
    /**
     * @param {number} index
     * @returns {boolean}
     */
    checkImgLoaded(index) {
      const nextItem = this.slotsItems()[index];
      if (!nextItem) {
        return true;
      }
      const imgs = nextItem.getElementsByTagName('img');
      let completed = true;
      if (imgs && imgs.length) {
        [...imgs].forEach((img) => {
          img.addEventListener('load', () => {
            if (!img.complete) completed = false;
          });
        });
      }
      return completed;
    },
    /**
     * flag = 1 : next
     * flag = -1 : prev
     */
    /**
     * @param {number} flag
     */
    move(flag) {
      if (!this.checkImgLoaded(this.currentPage + flag)) return false;
      if (this.sliding) return false;
      this.sliding = true;
      const direction = flag < 0 ? 'left' : 'right';

      if ((flag < 0 && this.currentPage > 1) || (flag > 0 && this.currentPage < this.numberOfPages)) {
        this.prevIndex = this.currentPage;
        this.currentPage += flag;
        this.scrollerX = this.scrollerPosition(this.prevIndex);
        this.changeDot(this.currentPage, true);
        this.$emit('slideshow-change', this.currentPage, direction);
        setTimeout(() => {
          this.sliding = false;
          this.prevIndex = -1;
          this.hasBeenDragged = false;
        }, 200);
      } else {
        this.prevIndex = this.currentPage + flag;
        this.scrollerX = this.scrollerPosition(this.currentPage + flag);
        this.$emit('slideshow-change', this.currentPage, direction);
        setTimeout(() => {
          this.currentPage = flag > 0 ? 1 : this.numberOfPages;
          this.changeDot(this.currentPage, true);
          this.noTransition = true;
          this.scrollerX = this.scrollerPosition();
          this.prevIndex = -1;
          this.$emit('slideshow-change', this.currentPage, direction);
          setTimeout(() => {
            this.noTransition = false;
            this.sliding = false;
            this.hasBeenDragged = false;
          }, 200);
        }, 200);
      }

      this.triggerAltImageClickOnce();

      return true;
    },
    /**
     * @param {number} index
     * @param {boolean} swiping
     */
    changeDot(index, swiping = false) {
      if (this.numberOfPages <= this.maxDotImageNumber) {
        this?.change(index);
        return;
      }
      const isLastItem = index === this.altDots[this.altDots.length - 1];
      const isFirstItem = index === this.altDots[0];

      if (isLastItem && index < this.numberOfPages) {
        this.altDots = this.altDots.map((r) => r + 1);
      }
      if (isFirstItem && index !== 1) {
        this.altDots = this.altDots.map((r) => r - 1);
      }
      if (swiping) {
        if (this.currentPage === 1) {
          this.altDots = this.arrayRange(1, this.maxDotImageNumber);
        } else if (this.currentPage === this.numberOfPages) {
          this.altDots = this.arrayRange(this.numberOfPages - (this.maxDotImageNumber - 1), this.numberOfPages);
        }
      } else this?.change(index);
      this.firstAltDotValue = this.altDots[0];
      this.triggerAltImageClickOnce();
      this.$emit('update-first-alt-dot', this.firstAltDotValue);
    },
    /**
     * @param {number} index
     */
    change(index) {
      if (!this.checkImgLoaded(index)) return;
      this.currentIndicator = index - 1;
      this.currentPage = index;
      if (this.currentPage === this.list.length) {
        this.triggerLastAltImageClickOnce();
      }
      this.scrollerX = this.scrollerPosition(index - 1);
      this.$emit('slideshow-change', this.currentPage);
      setTimeout(() => {
        this.currentIndicator = null;
      }, 100);
    },
    numberSuffix(number) {
      const suffixes = {
        one: 'st',
        two: 'nd',
        few: 'rd',
        other: 'th',
      };
      return suffixes[new Intl.PluralRules('en', { type: 'ordinal' }).select(number)];
    },
    /**
     * @param {number} activePage
     * @return {number}
     */
    scrollerPosition(activePage) {
      return -(this.childWidth * (activePage !== undefined ? activePage : this.currentPage));
    },
    /**
     * @return {string[]}
     */
    slotsItems() {
      if (this.$refs['v-scroller']) {
        return [...this.$refs['v-scroller'].getElementsByClassName('slideshow-item')];
      }
      return [];
    },
    reset() {
      this.scrollerWidth = null;
      this.slotsItems().forEach((item) => {
        item.style.width = null;
      });
    },
    doSizing() {
      // Condition is for code below not to fail, when it tries to run
      // in case component is no lenger in DOM
      if (!this.$refs['v-slideshow-wrapper']) return;
      this.stopAutoplay();
      this.numberOfPages = this.list.length;
      this.altDots = this.arrayRange(this.firstAltDotValue, (this.list || []).slice(0, this.maxDotImageNumber + (this.firstAltDotValue - 1)).length);
      this.itemsLength = this.numberOfPages + 2;
      this.wrapWidth = this.$refs['v-slideshow-wrapper'].getBoundingClientRect().width;
      this.childWidth = this.$refs[`v-slideshow-${this.id}`]?.getBoundingClientRect().width;
      this.slotsItems().forEach((item) => {
        item.style.width = `${this.childWidth}px`;
      });
      this.scrollerWidth = this.itemsLength * this.childWidth;
      this.scrollerX = this.scrollerPosition(this.currentPage - 1);
      if (this.autoplayMode === 'auto') {
        this.startAutoplay();
      }
    },
    init() {
      this.reset();
      this.$nextTick(() => {
        this.doSizing();
        this.$emit('load');
      });
    },
    eventHandler() {
      this.ro = new ResizeObserver(this.init);
      this.ro.observe(this.$refs[`v-slideshow-${this.id}`]);
    },
    scrollMouseenter() {
      this.hoverState = true;
      this.onEnter();
    },
    onEnter() {
      if (this.buttonFocus || this.hoverState) {
        if (this.autoplayMode === 'auto') this.stopAutoplay();
        else if (this.autoplayMode === 'manual-hover') this.startAutoplay();
      }
    },
    scrollMouseleave(event) {
      const target = event?.relatedTarget?.className;
      if (target && target !== 'video-control-play-dark-medium') {
        this.hoverState = false;
        this.onLeave();
      }
    },
    onLeave() {
      if (!this.buttonFocus && !this.hoverState) {
        this.isFocus = false;
        if (this.autoplayMode === 'auto') this.startAutoplay();
        else if (this.autoplayMode === 'manual-hover') {
          this.stopAutoplay();
          if (this.backToFirst) {
            this?.change(1);
          }
        } else if (this.autoplayMode === 'manual' && this.backToFirst) {
          this?.change(1);
        }
      }
    },
    startAutoplay() {
      this.showControl = 'pause';
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.move(1);
      }, this.autoplayInterval);
    },
    stopAutoplay() {
      this.showControl = 'play';
      clearInterval(this.timer);
      this.timer = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@csgIcons/new/video-control-play-dark-medium';
@import '~@csgIcons/new/video-control-pause-dark-medium';
@import '~@core/csg/src/scss/components/button-icon-mixin.scss';
@include csg-button-icon;

$transition: transform 300ms cubic-bezier(0.1, 0.57, 0.1, 1);

.v-slideshow-content {
  position: relative;
  overflow: hidden;
}

.slideshow-item {
  visibility: hidden;
  list-style-type: none;
  &.active {
    visibility: visible;
  }
}

.v-slideshow-wrapper {
  overflow: hidden;
  position: relative;
  .v-scroller {
    -webkit-transition: $transition;
    -moz-transition: $transition;
    -ms-transition: $transition;
    -o-transition: $transition;
    transition: $transition;
  }
}

.v-dot-indicator-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  .button-control {
    margin-right: rem-calc(8);
  }
  li {
    display: inline-block;
    border-radius: 50%;
    opacity: 1;
    padding: rem-calc(2);
    margin-right: rem-calc(8);
    cursor: pointer;
    background-color: $gray-2-color;
    border: rem-calc(1) solid $gray-2-color;
    &.active {
      background-color: $black;
      border: rem-calc(1) solid $black;
    }
  }
  &.mobile-dots {
    li {
      &:not(.active) {
        &:first-child,
        &:last-child {
          padding: rem-calc(1);
          height: rem-calc(4);
        }
      }
    }
  }
}
</style>
